import React from "react";
import {
  Box,
  Typography,
  Grid,
  IconButton,
  Tooltip,
  useTheme,
  useMediaQuery,
  Paper,
} from "@mui/material";
import { Facebook, Twitter, LinkedIn } from "@mui/icons-material";
import { useTranslation } from "react-i18next";

const Footer = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const socialLinks = {
    Facebook: "https://www.facebook.com/profile.php?id=100008080722750",
    Twitter: "https://x.com/MedEngSa",
    LinkedIn: "https://www.linkedin.com/in/med-eng-9b6576b0/",
  };

  const renderCard = (children, delay = 0) => (
    <Paper
      elevation={12}
      sx={{
        background: "linear-gradient(160deg, #1e2b3a, #141c29)",
        color: "#fff",
        p: 4,
        borderRadius: "20px",
        boxShadow: "0 20px 40px rgba(0,0,0,0.6)",
        transform: "perspective(1000px) rotateX(2deg) rotateY(-2deg)",
        transition: "all 0.4s ease",
        "&:hover": {
          transform: "perspective(1000px) rotateX(0deg) rotateY(0deg) scale(1.02)",
          boxShadow: "0 25px 50px rgba(255,255,255,0.1)",
        },
        animation: `fadeInUp 0.6s ease ${delay}s both`,
        position: "relative",
        zIndex: 1,
      }}
    >
      {children}
    </Paper>
  );

  return (
    <Box
      sx={{
        background: "linear-gradient(145deg, #0f1622, #1a2636)",
        pt: 10,
        pb: 6,
        px: isMobile ? 3 : 10,
        mt: 12,
        borderTopLeftRadius: "112px",
        borderTopRightRadius: "112px",
        overflow: "hidden",
        position: "relative",
      }}
    >
      {/* Background glow */}
      <Box
        sx={{
          position: "absolute",
          top: "-30%",
          left: "50%",
          width: "300%",
          height: "300%",
          background:
            "radial-gradient(circle at center, rgba(255,255,255,0.04), transparent 60%)",
          transform: "translateX(-50%)",
          zIndex: 0,
        }}
      />

      {/* Cards */}
      <Grid container spacing={4} justifyContent="center" sx={{ position: "relative", zIndex: 2 }}>
        {/* Company Info */}
        <Grid item xs={12} md={4}>
          {renderCard(
            <>
              <Typography variant="h5" fontWeight="bold" gutterBottom>
                {t("footer.company")}
              </Typography>
              <Typography variant="body2" sx={{ color: "#bbb", lineHeight: 1.6 }}>
                {t("footer.description")}
              </Typography>
              <Box mt={3}>
                {Object.entries(socialLinks).map(([key, url], index) => {
                  const Icon = { Facebook, Twitter, LinkedIn }[key];
                  return (
                    <Tooltip title={key} key={index}>
                      <IconButton
                        component="a"
                        href={url}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{
                          color: "#bbb",
                          backdropFilter: "blur(6px)",
                          border: "1px solid rgba(255,255,255,0.1)",
                          transition: "0.3s",
                          borderRadius: "10px",
                          "&:hover": {
                            color: "#fff",
                            transform: "scale(1.2)",
                            boxShadow: "0 6px 16px rgba(255,255,255,0.2)",
                          },
                          mr: 1,
                        }}
                      >
                        <Icon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  );
                })}
              </Box>
            </>,
            0.1
          )}
        </Grid>

        {/* Contact Info */}
        <Grid item xs={12} md={4}>
          {renderCard(
            <>
              <Typography variant="h6" fontWeight="bold" gutterBottom>
                {t("footer.contact")}
              </Typography>
              {[
                t("footer.address"),
                { label: t("footer.tel"), value: "+966 1 4800916 / +966 1 4800918 / +966 1 4887491" },
                { label: t("footer.fax"), value: "+966 1 4829511 / +966 1 4823362" },
                { label: t("footer.cell"), value: "00966553031034 / 00966553010210" },
                { label: t("footer.email"), value: "info@tatweer-med.com" },
              ].map((item, index) =>
                typeof item === "string" ? (
                  <Typography
                    key={index}
                    variant="body2"
                    sx={{
                      color: "#bbb",
                      mb: 1,
                      "&:hover": { color: "#fff" },
                    }}
                  >
                    {item}
                  </Typography>
                ) : (
                  <Typography
                    key={index}
                    variant="body2"
                    sx={{
                      direction: "ltr",
                      unicodeBidi: "plaintext",
                      color: "#bbb",
                      mb: 1,
                      "&:hover": { color: "#fff" },
                    }}
                  >
                    <Box component="span" fontWeight="bold" fontSize={15} color="white">
                      {item.label}:
                    </Box>{" "}
                    {item.value}
                  </Typography>
                )
              )}
            </>,
            0.2
          )}
        </Grid>

        {/* Vision Image */}
        <Grid item xs={12} md={4}>
          {renderCard(
            <Box textAlign="center">
              <Typography variant="h6" fontWeight="bold" gutterBottom>
                {t("footer.vision")}
              </Typography>
              <img
                src="/image/Vision1.png"
                alt="Saudi Vision 2030"
                style={{
                  width: isMobile ? "140px" : "180px",
                  opacity: 0.85,
                  filter: "contrast(1.2) brightness(1.1)",
                  marginTop: 10,
                }}
              />
            </Box>,
            0.3
          )}
        </Grid>
      </Grid>

      {/* Copyright */}
      <Box mt={6} textAlign="center" sx={{ zIndex: 2, position: "relative" }}>
        <Typography variant="caption" sx={{ color: "#aaa", fontSize: "0.75rem" }}>
          {t("footer.copyright")}
        </Typography>
      </Box>

      <style>
        {`
          @keyframes fadeInUp {
            from {
              opacity: 0;
              transform: translate3d(0, 40px, 0);
            }
            to {
              opacity: 1;
              transform: none;
            }
          }
        `}
      </style>
    </Box>
  );
};

export default Footer;
